import React from 'react'
import { Button, Checkbox, Spin, Typography, message, Alert } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import { seoPageApi } from 'api/seoPages'
import {
  ISubCategoryFilter,
  ISelectedFilter,
  IFilterValueCount
} from 'models/seoPages'
import { showServerError } from 'shared/utils/showServerError'
import styles from './FilterSelector.module.scss'

interface IFilterSelectorProps {
  subCategoryId?: string
  selectedFilters: ISelectedFilter[]
  onChange: (filters: ISelectedFilter[]) => void
}

const countsKey = (name: string, value: string) => `${name}|${value}`

// Счётчики показываем, чтобы не собрать страницу из значений, по которым
// в наличии нет ни одного товара: 19 из 22 страниц вышли пустыми
const fetchFiltersWithCounts = async (subCategoryId: string) => {
  const [filtersResponse, countsResponse] = await Promise.all([
    seoPageApi.getFilters(subCategoryId),
    seoPageApi
      .getFilterValuesCount(subCategoryId)
      .catch(() => ({ data: [] as IFilterValueCount[] }))
  ])

  return {
    filters: filtersResponse.data,
    counts: new Map(
      countsResponse.data.map((item) => [
        countsKey(item.name, item.value),
        item.count
      ])
    )
  }
}

export const FilterSelector: React.FC<IFilterSelectorProps> = React.memo(({
  subCategoryId,
  selectedFilters,
  onChange
}) => {
  const [filters, setFilters] = React.useState<ISubCategoryFilter[]>([])
  const [counts, setCounts] = React.useState<Map<string, number>>(new Map())
  const [isLoading, setIsLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (!subCategoryId) {
      setFilters([])
      setCounts(new Map())
      setError(null)
      onChange([])
      return
    }

    const loadFilters = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await fetchFiltersWithCounts(subCategoryId)
        setFilters(data.filters)
        setCounts(data.counts)
      } catch (error) {
        setError('Не удалось загрузить фильтры')
        setFilters([])
        setCounts(new Map())
        showServerError(error)
        message.error('Не удалось загрузить фильтры')
      } finally {
        setIsLoading(false)
      }
    }

    loadFilters()
  }, [subCategoryId, onChange])

  const handleFilterChange = (filterName: string, selectedValues: string[]) => {
    const updatedFilters = selectedFilters.filter(f => f.name !== filterName)

    if (selectedValues.length > 0) {
      updatedFilters.push({
        name: filterName,
        selectedValues
      })
    }

    onChange(updatedFilters)
  }

  const handleSelectAll = (filterName: string, allValues: string[]) => {
    handleFilterChange(filterName, allValues)
  }

  const handleClearAll = (filterName: string) => {
    handleFilterChange(filterName, [])
  }

  const getSelectedValues = (filterName: string): string[] => {
    const filter = selectedFilters.find(f => f.name === filterName)
    return filter?.selectedValues || []
  }

  const retryLoadFilters = () => {
    if (!subCategoryId) return

    const loadFilters = async () => {
      try {
        setIsLoading(true)
        setError(null)
        const data = await fetchFiltersWithCounts(subCategoryId)
        setFilters(data.filters)
        setCounts(data.counts)
      } catch (error) {
        setError('Не удалось загрузить фильтры')
        setFilters([])
        setCounts(new Map())
        showServerError(error)
        message.error('Не удалось загрузить фильтры')
      } finally {
        setIsLoading(false)
      }
    }

    loadFilters()
  }

  if (!subCategoryId) {
    return (
      <div className={styles.emptyState}>
        <Typography.Text type='secondary'>
          Выберите подкатегорию для загрузки фильтров
        </Typography.Text>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <Spin size='large' />
        <Typography.Text type='secondary'>Загрузка фильтров...</Typography.Text>
      </div>
    )
  }

  if (error && !isLoading) {
    return (
      <div className={styles.container}>
        <Typography.Title level={4} className={styles.title}>
          Фильтры
        </Typography.Title>
        <Alert
          message='Ошибка загрузки фильтров'
          description={error}
          type='error'
          showIcon
          action={
            <Button
              size='small'
              icon={<ReloadOutlined />}
              onClick={retryLoadFilters}
            >
              Повторить
            </Button>
          }
        />
      </div>
    )
  }

  if (filters.length === 0 && !isLoading && !error) {
    return (
      <div className={styles.emptyState}>
        <Typography.Text type='secondary'>
          Для данной подкатегории фильтры не найдены
        </Typography.Text>
      </div>
    )
  }

  return (
    <div className={styles.container}>
      <Typography.Title level={4} className={styles.title}>
        Фильтры
      </Typography.Title>

      {filters.map((filter) => {
        const selectedValues = getSelectedValues(filter.name)
        const isAllSelected = selectedValues.length === filter.values.length

        return (
          <div key={filter.name} className={styles.filterGroup}>
            <div className={styles.filterHeader}>
              <Typography.Text strong>{filter.name}</Typography.Text>
              <div className={styles.filterActions}>
                <Button
                  type='link'
                  size='small'
                  onClick={() => handleSelectAll(filter.name, filter.values)}
                  disabled={isAllSelected}
                >
                  Выбрать все
                </Button>
                <Button
                  type='link'
                  size='small'
                  onClick={() => handleClearAll(filter.name)}
                  disabled={selectedValues.length === 0}
                >
                  Очистить
                </Button>
              </div>
            </div>

            <Checkbox.Group
              value={selectedValues}
              onChange={(values) => handleFilterChange(filter.name, values as string[])}
              className={styles.checkboxGroup}
            >
              {filter.values.map((value) => {
                const count = counts.get(countsKey(filter.name, value))

                return (
                  <Checkbox
                    key={value}
                    value={value}
                    className={styles.checkbox}
                  >
                    {value}
                    {count !== undefined && (
                      <Typography.Text
                        type={count > 0 ? 'secondary' : 'danger'}
                        className={styles.count}
                      >
                        {` · ${count}`}
                      </Typography.Text>
                    )}
                  </Checkbox>
                )
              })}
            </Checkbox.Group>
          </div>
        )
      })}
    </div>
  )
})